import { useEffect, useRef, useState } from 'react';
import { apiClient, ApiError } from '@/lib/api/client';
import { ShaderAnimation } from '@/components/ShaderAnimation';

type Stage = 'form' | 'sending' | 'sent' | 'error';

interface Props {
  initialError?: string | null;
}

const ERROR_COPY: Record<string, string> = {
  expired_link: 'That sign-in link expired or was already used. Request a new one below.',
  missing_token: 'Missing sign-in token. Try requesting a new link.',
  internal_error:
    "We couldn't verify that link. It may have already been used. Request a fresh one below.",
  too_many_requests: 'Too many sign-in attempts. Take a breath, then try again in an hour.',
};

export function SignInScreen({ initialError }: Props) {
  const [stage, setStage] = useState<Stage>('form');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(
    initialError ? (ERROR_COPY[initialError] ?? null) : null,
  );
  // Anchor for moving focus when stage transitions — keyboard + screen reader
  // users need a focused element to land on after the form is replaced.
  const sentPanelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (stage === 'sent') {
      sentPanelRef.current?.focus();
    }
  }, [stage]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setStage('sending');
    setError(null);
    try {
      await apiClient.requestMagicLink(email.trim());
      setStage('sent');
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? (ERROR_COPY[err.code] ?? err.message)
          : 'Network problem. Check your connection.';
      setError(msg);
      setStage('error');
    }
  }

  return (
    <main className="relative min-h-dvh overflow-hidden bg-background text-foreground antialiased">
      {/* Full-bleed three.js shader background. Absolutely positioned so
          the sign-in form sits on top via relative+z-10 wrappers. */}
      <ShaderAnimation />
      {/* Radial vignette only behind the form card so the shader stays
          vibrant everywhere except where the copy needs contrast. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 45%, transparent 75%)',
        }}
      />
      <div className="relative z-10 mx-auto flex min-h-dvh max-w-md flex-col items-stretch justify-center px-6 py-16">
        <div className="space-y-10">
          <div className="space-y-6">
            {/* Centered + large APETech 2026 wordmark with transparent
                background so the shader shows through. Decorative — h1
                below carries the accessible name. */}
            <div className="flex justify-center">
              <img
                src="/apetech-logo-2026.png"
                alt=""
                aria-hidden
                className="h-auto w-full max-w-[360px] drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)]"
              />
            </div>
            <div className="space-y-2 pt-2 text-center">
              <h1 className="text-balance text-4xl font-semibold tracking-tight md:text-5xl">
                Your ops inbox.
              </h1>
              <p className="mx-auto max-w-sm text-balance text-base text-muted-foreground">
                Ingest events from any system. Route, triage, resolve. One queue instead of five
                dashboards.
              </p>
            </div>
          </div>

          {/* Persistent live region for errors. Mounted unconditionally so
              screen readers reliably announce when `error` flips from null
              to a message; conditionally mounting the role=alert misses
              dynamic updates on most SR engines. */}
          <div
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            className={error ? 'rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-foreground' : 'sr-only'}
          >
            {error ?? ''}
          </div>

          {stage === 'sent' ? (
            <div
              ref={sentPanelRef}
              tabIndex={-1}
              role="status"
              aria-live="polite"
              className="rounded-xl border border-border/60 bg-card/85 p-6 backdrop-blur-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
            >
              <div className="space-y-3">
                <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Check your email
                </div>
                <div className="text-base text-foreground">
                  Link sent to <span className="font-mono text-sm">{email}</span>.
                </div>
                <p className="text-sm text-muted-foreground">
                  Valid for 10 minutes, works once. From{' '}
                  <span className="font-mono">noreply@mail.apetech.studio</span>. Check spam
                  if it doesn't land in 30 seconds.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setStage('form');
                    setError(null);
                    setEmail('');
                  }}
                  className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                >
                  Send to a different email
                </button>
              </div>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="space-y-4 rounded-xl border border-border/60 bg-card/70 p-6 backdrop-blur-xl"
            >
              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className="block font-mono text-[11px] uppercase tracking-widest text-muted-foreground"
                >
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="off"
                  spellCheck={false}
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@apete.ch"
                  className="block w-full rounded-lg border border-border bg-card px-4 py-3 text-base text-foreground placeholder:text-muted-foreground/60 focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30"
                />
              </div>
              <button
                type="submit"
                disabled={stage === 'sending'}
                aria-busy={stage === 'sending'}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {stage === 'sending' && (
                  <span
                    aria-hidden
                    className="size-3 animate-spin rounded-full border-2 border-primary-foreground/40 border-t-primary-foreground"
                  />
                )}
                {stage === 'sending' ? 'Sending…' : 'Email me a sign-in link'}
              </button>
              <p className="text-center text-xs text-muted-foreground">
                No password. We email you a link valid for 10 minutes.
              </p>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
